import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import { publicAnnouncementApi } from '@/api/publicAnnouncements'

function formatDate(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { date: iso, time: '' }
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return { date: `${y}.${m}.${day}`, time: `${hh}:${mm}:${ss}` }
}

export default function AnnouncementDetailPage() {
  const { t } = useTranslation('announcements')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const numericId = Number(id)
  const isValidId = Number.isFinite(numericId) && numericId > 0

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-announcement', numericId],
    queryFn: () => publicAnnouncementApi.getById(numericId),
    enabled: isValidId,
  })

  const sanitizedHtml = useMemo(() => {
    if (!data) return ''
    if (data.contentFormat === 'html') return DOMPurify.sanitize(data.content)
    const div = document.createElement('div')
    div.textContent = data.content
    return div.innerHTML.replace(/\n/g, '<br/>')
  }, [data])

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1)
    else navigate('/announcements')
  }

  if (isLoading) {
    return (
      <section className="max-w-[1136px] mx-auto px-6 py-20 text-center text-[#a0a0a0]">
        {t('detail.loading')}
      </section>
    )
  }

  if (!isValidId || isError || !data) {
    return (
      <section className="max-w-[1136px] mx-auto px-6 py-20 text-center">
        <p className="text-[#131313] mb-6">{t('detail.notFound')}</p>
        <button
          type="button"
          onClick={() => navigate('/announcements')}
          className="px-5 h-10 rounded-full bg-[#4599ff] text-white text-sm font-medium hover:brightness-95"
        >
          {t('detail.back')}
        </button>
      </section>
    )
  }

  const { date, time } = formatDate(data.createdAt)

  return (
    <section className="max-w-[1136px] mx-auto pt-4 pb-20 lg:pt-16 lg:pb-[120px]">
      <h1 className="px-6 lg:px-0 mb-10 lg:mb-20 text-center text-2xl lg:text-5xl font-bold text-[#131313] tracking-[-0.6px] leading-[1.4] lg:leading-[1.2]">
        {t('title')}
      </h1>

      <div className="flex flex-col gap-6 lg:gap-10 px-6 lg:px-0">
        <header className="flex flex-col gap-4 lg:gap-6">
          <h2 className="text-xl lg:text-[32px] font-bold text-[#131313] leading-[1.5] lg:leading-[1.4] tracking-[-0.6px]">
            {data.title}
          </h2>
          <div className="flex items-center gap-4 text-sm lg:text-base">
            {data.isPinned ? (
              <span className="text-[#4599ff] font-semibold tracking-[-0.6px]">
                {t('category.pinned')}
              </span>
            ) : (
              <span className="text-[#494949] font-normal tracking-[-0.6px]">
                {t('category.normal')}
              </span>
            )}
            <span className="text-[#a0a0a0] font-normal tracking-[-0.6px]">
              {t('detail.createdAt')} : {date} / {time}
            </span>
          </div>
        </header>

        <div
          className="tiptap-viewer border-y border-[#dadada] pt-6 pb-10 lg:pt-10 lg:pb-20 text-base lg:text-lg font-normal text-[#131313] leading-[1.5] tracking-[-0.6px]"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />

        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleBack}
            className="h-14 max-w-[432px] min-w-[120px] w-full px-6 rounded-lg bg-[#f4f4f4] border border-[#dadada] text-base lg:text-xl font-semibold text-[#494949] tracking-[-0.6px] hover:brightness-95"
          >
            {t('detail.back')}
          </button>
        </div>
      </div>
    </section>
  )
}
